import React, { useMemo, useState } from 'react';
import { ParsedBiomarker } from '../../../api/corevitals';
import './ExtractedDataTable.scss';

interface ExtractedDataTableProps {
  biomarkers: ParsedBiomarker[];
  summary?: Record<string, any>;
  responseData?: Record<string, any>;
  missingList?: Array<{ biomarker_name: string; standard_unit: string }>;
  onBiomarkersUpdate?: (updatedBiomarkers: any[]) => void;
}

const PREFERRED_COLUMN_ORDER = [
  'Biomarker Name',
  'Report Name',
  'Biomarker Value',
  'Report value',
  'Biomarker Unit',
  'Report Unit',
  'Conversion Note'
];

const ExtractedDataTable: React.FC<ExtractedDataTableProps> = ({ 
  biomarkers, 
  summary, 
  responseData, 
  missingList = [],
  onBiomarkersUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'matched' | 'missing'>('matched');
  const [localBiomarkers, setLocalBiomarkers] = useState<any[]>(biomarkers);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  // Update local biomarkers when props change
  React.useEffect(() => {
    setLocalBiomarkers(biomarkers);
    console.log('Biomarkers updated:', biomarkers.length, biomarkers);
    console.log('First biomarker keys:', biomarkers.length > 0 ? Object.keys(biomarkers[0]) : 'No biomarkers');
  }, [biomarkers]);

  // Get all unique keys from all biomarkers to create dynamic columns
  const allColumns = useMemo(() => {
    const columnSet = new Set<string>();
    localBiomarkers.forEach((biomarker) => {
      Object.keys(biomarker).forEach((key) => columnSet.add(key));
    });
    
    // Sort columns: preferred order first, then others alphabetically
    const allKeys = Array.from(columnSet);
    const preferredColumns = PREFERRED_COLUMN_ORDER.filter(key => allKeys.includes(key));
    const otherColumns = allKeys
      .filter(key => !PREFERRED_COLUMN_ORDER.includes(key))
      .sort();
    
    return [...preferredColumns, ...otherColumns, 'actions'];
  }, [localBiomarkers]);

  // Handle delete row
  const handleDeleteRow = (index: number) => {
    const updated = localBiomarkers.filter((_, i) => i !== index);
    setLocalBiomarkers(updated);
    if (onBiomarkersUpdate) {
      onBiomarkersUpdate(updated);
    }
  };

  // Handle start editing
  const handleStartEdit = (index: number, currentValue: string) => {
    setEditingIndex(index);
    setEditingValue(currentValue);
  };

  // Handle save edit
  const handleSaveEdit = (index: number) => {
    const updated = [...localBiomarkers];
    updated[index] = {
      ...updated[index],
      'Biomarker Value': editingValue
    };
    setLocalBiomarkers(updated);
    setEditingIndex(null);
    setEditingValue('');
    if (onBiomarkersUpdate) {
      onBiomarkersUpdate(updated);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  // Format column name for display
  const formatColumnName = (key: string): string => {
    // Handle Title Case keys from API
    if (key === 'Biomarker Name') return 'Biomarker Name';
    if (key === 'Report Name') return 'Report Name';
    if (key === 'Biomarker Value') return 'Biomarker Value';
    if (key === 'Report value') return 'original value';
    if (key === 'Biomarker Unit') return 'Biomarker Unit';
    if (key === 'Report Unit') return 'Original Unit';
    if (key === 'Conversion Note') return 'Conversion Note';
    
    // Fallback for snake_case or other formats
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format cell value for display
  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderSummary = () => {
    if (!summary || Object.keys(summary).length === 0) {
      return null;
    }

    return (
      <div className="extracted-data-summary">
        <div className="summary-title">Summary</div>
        <div className="summary-content">
          {Object.entries(summary).map(([key, value]) => (
            <div key={key} className="summary-item">
              <span className="summary-key">{formatColumnName(key)}:</span>
              <span className="summary-value">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderResponseData = () => {
    if (!responseData || Object.keys(responseData).length === 0) {
      return null;
    }

    // Filter out biomarkers and summary as they're displayed separately
    const otherData = Object.entries(responseData).filter(
      ([key]) => key !== 'biomarkers' && key !== 'summary'
    );

    if (otherData.length === 0) {
      return null;
    }

  };

  // Get columns for missing list
  const missingColumns = useMemo(() => {
    if (missingList.length === 0) return [];
    const columnSet = new Set<string>();
    missingList.forEach((item) => {
      Object.keys(item).forEach((key) => columnSet.add(key));
    });
    return Array.from(columnSet);
  }, [missingList]);

  return (
    <div className="extracted-data-table-container">
      {renderSummary()}
      {renderResponseData()}
      
      <div className="extracted-biomarkers-section">
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'matched' ? 'active' : ''}`}
            onClick={() => setActiveTab('matched')}
          >
            Matched ({localBiomarkers.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'missing' ? 'active' : ''}`}
            onClick={() => setActiveTab('missing')}
          >
            Missing ({missingList.length})
          </button>
        </div>

        {activeTab === 'matched' && (
          <>
            {localBiomarkers.length > 0 ? (
              <div className="biomarkers-table">
                <div className="table-header" style={{ gridTemplateColumns: `${allColumns.filter(c => c !== 'actions').map(() => '1fr').join(' ')} 60px` }}>
                  {allColumns.map((column) => (
                    <div key={column} className="header-cell" title={column}>
                      {column === 'actions' ? '' : formatColumnName(column)}
                    </div>
                  ))}
                </div>
                <div 
                  className="table-body" 
                  style={{ 
                    display: 'block', 
                    visibility: 'visible',
                    minHeight: '300px',
                    height: 'auto',
                    padding: '0',
                    margin: '0',
                    overflow: 'auto',
                    maxHeight: 'calc(90vh - 500px)'
                  }}
                >
                  {localBiomarkers.map((biomarker, index) => {
                    if (index === 0) {
                      console.log('Rendering table body with', localBiomarkers.length, 'rows');
                      console.log(`First row data:`, biomarker);
                    }
                    return (
                    <div 
                      key={index} 
                      className="table-row"
                      style={{ gridTemplateColumns: `${allColumns.filter(c => c !== 'actions').map(() => '1fr').join(' ')} 60px` }}
                    >
                      {allColumns.map((column) => {
                        if (column === 'actions') {
                          return (
                            <div key={column} className="cell actions-cell">
                              <button
                                className="delete-button"
                                onClick={() => handleDeleteRow(index)}
                                title="Delete row"
                              >
                                ×
                              </button>
                            </div>
                          );
                        }

                        const value = (biomarker as any)[column];
                        const displayValue = formatCellValue(value);
                        const isEditing = editingIndex === index && column === 'Biomarker Value';
                        
                        // Debug log for first row
                        if (index === 0 && column === allColumns[0]) {
                          console.log('Rendering cell:', { column, value, displayValue, biomarker });
                        }

                        return (
                          <div 
                            key={column} 
                            className={`cell ${column === 'Biomarker Value' ? 'editable-cell' : ''}`}
                            title={column === 'Biomarker Value' ? `${displayValue} - Double click to edit` : displayValue}
                            onDoubleClick={() => column === 'Biomarker Value' && handleStartEdit(index, value || '')}
                            onClick={(e) => {
                              if (column === 'Biomarker Value' && !isEditing) {
                                e.stopPropagation();
                              }
                            }}
                          >
                            {isEditing ? (
                              <div className="inline-edit-container">
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleSaveEdit(index)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveEdit(index);
                                    } else if (e.key === 'Escape') {
                                      e.preventDefault();
                                      handleCancelEdit();
                                    }
                                  }}
                                  className="inline-edit-input"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="edit-actions">
                                  <button
                                    type="button"
                                    className="save-edit-button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSaveEdit(index);
                                    }}
                                    title="Save (Enter)"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type="button"
                                    className="cancel-edit-button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleCancelEdit();
                                    }}
                                    title="Cancel (Esc)"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span className={column === 'Biomarker Value' ? 'editable-value' : ''}>
                                <span className="value-wrap" >{displayValue}</span>
                                {column === 'Biomarker Value' && (
                                  <span className="edit-hint"> (Double-click to edit)</span>
                                )}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-message">No matched biomarkers found</div>
            )}
          </>
        )}

        {activeTab === 'missing' && (
          <>
            {missingList.length > 0 ? (
              <div className="biomarkers-table">
                <div className="table-header" style={{ gridTemplateColumns: `repeat(${missingColumns.length}, 1fr)` }}>
                  {missingColumns.map((column) => (
                    <div key={column} className="header-cell" title={column}>
                      {formatColumnName(column)}
                    </div>
                  ))}
                </div>
                <div className="table-body">
                  {missingList.map((item, index) => (
                    <div 
                      key={index} 
                      className="table-row"
                      style={{ gridTemplateColumns: `repeat(${missingColumns.length}, 1fr)` }}
                    >
                      {missingColumns.map((column) => {
                        const value = (item as any)[column];
                        const displayValue = formatCellValue(value);
                        return (
                          <div 
                            key={column} 
                            className="cell" 
                            title={displayValue}
                          >
                            {displayValue}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-message">No missing biomarkers</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExtractedDataTable;

